import { Preferences } from '@capacitor/preferences';

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const API_TOKEN_KEY = "api_token";

// In-memory cache für synchronen Zugriff
let cachedAccessToken = null;
let cachedRefreshToken = null;
let cachedApiToken = null;

// Initialisierung: Lade Tokens aus Preferences beim App-Start
let initialized = false;

export const initTokenStorage = async () => {
  if (initialized) return;

  try {
    const [accessResult, refreshResult, apiResult] = await Promise.all([
      Preferences.get({ key: ACCESS_TOKEN_KEY }),
      Preferences.get({ key: REFRESH_TOKEN_KEY }),
      Preferences.get({ key: API_TOKEN_KEY }),
    ]);

    cachedAccessToken = accessResult.value;
    cachedRefreshToken = refreshResult.value;
    cachedApiToken = apiResult.value;

    console.log('TokenStorage initialized:', {
      hasAccessToken: !!cachedAccessToken,
      hasRefreshToken: !!cachedRefreshToken,
      hasApiToken: !!cachedApiToken,
    });

    initialized = true;
  } catch (error) {
    console.error('Failed to initialize token storage:', error);
    // Fallback to localStorage for web
    cachedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    cachedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    cachedApiToken = localStorage.getItem(API_TOKEN_KEY);
    initialized = true;
  }
};

export const setTokens = async (accessToken, refreshToken) => {
  // WICHTIG: Cache SOFORT setzen, damit nachfolgende synchrone Aufrufe den Wert haben
  if (accessToken) {
    cachedAccessToken = accessToken;
  }
  if (refreshToken) {
    cachedRefreshToken = refreshToken;
  }
  console.log('Tokens cached immediately');

  try {
    const promises = [];
    if (accessToken) {
      promises.push(Preferences.set({ key: ACCESS_TOKEN_KEY, value: accessToken }));
    }
    if (refreshToken) {
      promises.push(Preferences.set({ key: REFRESH_TOKEN_KEY, value: refreshToken }));
    }
    await Promise.all(promises);
    console.log('Tokens persisted to Preferences');
  } catch (error) {
    console.error('Failed to persist tokens:', error);
    // Fallback to localStorage
    if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

export const getAccessToken = () => {
  // Synchroner Zugriff auf gecachten Wert
  return cachedAccessToken;
};

export const getRefreshToken = () => {
  return cachedRefreshToken;
};

export const clearTokens = async () => {
  try {
    await Promise.all([
      Preferences.remove({ key: ACCESS_TOKEN_KEY }),
      Preferences.remove({ key: REFRESH_TOKEN_KEY }),
      Preferences.remove({ key: API_TOKEN_KEY }),
    ]);
    console.log('All tokens cleared from Preferences');
  } catch (error) {
    console.error('Failed to clear tokens from Preferences:', error);
    // Fallback to localStorage
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(API_TOKEN_KEY);
  }

  cachedAccessToken = null;
  cachedRefreshToken = null;
  cachedApiToken = null;
};

// API Token (für QR-Code basierte Anmeldung)
export const setApiToken = async (token) => {
  if (token) {
    // WICHTIG: Cache SOFORT setzen, damit nachfolgende synchrone Aufrufe den Wert haben
    cachedApiToken = token;
    console.log('API Token cached immediately:', token.substring(0, 8) + '...');

    try {
      await Preferences.set({ key: API_TOKEN_KEY, value: token });
      console.log('API Token persisted to Preferences');
    } catch (error) {
      console.error('Failed to persist API token:', error);
      // Fallback to localStorage
      localStorage.setItem(API_TOKEN_KEY, token);
    }
  }
};

export const getApiToken = () => {
  return cachedApiToken;
};

export const clearApiToken = async () => {
  try {
    await Preferences.remove({ key: API_TOKEN_KEY });
  } catch (error) {
    console.error('Failed to clear API token:', error);
    localStorage.removeItem(API_TOKEN_KEY);
  }
  cachedApiToken = null;
};
